import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FolderOpen } from 'lucide-react';

const FOLDER_COLORS = [
  '#6a1fbf', '#00c853', '#ff5722', '#2196f3',
  '#9c27b0', '#ff9800', '#e91e63', '#00bcd4'
];

export default function ElementFolderModal({ isOpen, onClose, onSave, folder }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6a1fbf');
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (folder) {
        setName(folder.name || '');
        setColor(folder.color || '#6a1fbf');
      } else {
        setName('');
        setColor('#6a1fbf');
      }
      setHasInitialized(true);
    }
  }, [isOpen, folder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && name.trim()) {
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${color}20` }}
              >
                <FolderOpen className="w-5 h-5" style={{ color }} />
              </div>
              <h2 className="text-lg font-semibold text-white">
                {folder ? 'Rename Folder' : 'Create Folder'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Folder Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Webinar Assets"
                autoFocus
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-2 block">Color</label>
              <div className="flex gap-2">
                {FOLDER_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-lg transition-all hover:scale-110 ${
                      color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#001233]' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-300 border border-white/20 rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim()}
              className="px-4 py-2 text-sm text-white bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg hover:from-[#7a2fcf] hover:to-[#00d863] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {folder ? 'Save Changes' : 'Create Folder'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Use portal to render at document root level
  return ReactDOM.createPortal(modalContent, document.body);
}
